import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';

const SavedItemsContext = createContext({
  savedKeys: new Set(),
  isSaved: () => false,
  toggleSave: () => {},
  loading: false,
});

export const useSavedItems = () => useContext(SavedItemsContext);

export function SavedItemsProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [savedKeys, setSavedKeys] = useState(new Set());
  const pendingRef = useRef(new Set()); // per-key in-flight guard
  const mountedRef = useRef(true);

  // Load all saved compound keys when user is authenticated
  useEffect(() => {
    mountedRef.current = true;
    if (!isAuthenticated) {
      setSavedKeys(new Set());
      return () => { mountedRef.current = false; };
    }
    (async () => {
      try {
        const { data } = await api.get('/api/saved-items?item_type=compound');
        if (mountedRef.current) {
          const keys = new Set(data.items.map(i => i.item_key));
          setSavedKeys(keys);
        }
      } catch (err) {
        // API unavailable or error – fail silently
        console.debug('Could not load saved items', err);
      }
    })();
    return () => { mountedRef.current = false; };
  }, [isAuthenticated]);

  const isSaved = useCallback((itemKey) => savedKeys.has(String(itemKey)), [savedKeys]);

  const toggleSave = useCallback(async (itemKey, compound) => {
    const key = String(itemKey);
    if (!key) return; // guard against empty keys
    if (pendingRef.current.has(key)) return; // already in-flight for this key
    pendingRef.current.add(key);

    const wasSaved = savedKeys.has(key);

    // Optimistic update — flip state immediately for instant UI feedback
    setSavedKeys(prev => {
      const next = new Set(prev);
      if (wasSaved) next.delete(key); else next.add(key);
      return next;
    });

    try {
      if (wasSaved) {
        await api.delete(`/api/saved-items/by-key/${encodeURIComponent(key)}?item_type=compound`);
      } else {
        let enrichedCompound = { ...(compound || {}) };
        if (compound?.compound_id) {
          try {
            const { data: details } = await api.get(`/compounds/${compound.compound_id}/details`);
            enrichedCompound = {
              ...enrichedCompound,
              price_range: details.price_range,
              unit_types: details.unit_types,
              total_available_units: details.total_available_units,
            };
          } catch (_) {
            // enrichment failed — save without unit data
          }
        }
        await api.post('/api/saved-items', {
          item_type: 'compound',
          item_key: key,
          item_data: enrichedCompound,
        });
      }
    } catch (err) {
      if (err.response?.status === 409) {
        // Already saved — keep it saved
        setSavedKeys(prev => new Set(prev).add(key));
      } else {
        console.error('Failed to toggle save', err);
        // Revert optimistic update on failure
        if (mountedRef.current) {
          setSavedKeys(prev => {
            const next = new Set(prev);
            if (wasSaved) next.add(key); else next.delete(key);
            return next;
          });
        }
      }
    } finally {
      pendingRef.current.delete(key);
    }
  }, [savedKeys]);

  return (
    <SavedItemsContext.Provider value={{ savedKeys, isSaved, toggleSave, loading: false }}>
      {children}
    </SavedItemsContext.Provider>
  );
}

export default SavedItemsContext;
